// src/utils/videoGate.ts
export const ALLOWED_VIDEO_MIME = new Set([
  'video/mp4',
  'video/quicktime', // MOV (often H.264 on iOS; may be HEVC on some devices)
]);

/**
 * Quick client-side gate:
 *  - require MP4/MOV container
 *  - try loading metadata + a brief play/pause probe (muted)
 * If it fails, throw with a user-friendly message.
 */
export async function ensureAllowedVideo(file: File): Promise<void> {
  if (!ALLOWED_VIDEO_MIME.has(file.type)) {
    throw new Error('Only MP4/MOV (H.264/AAC) are allowed.');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    v.playsInline = true;
    v.src = objectUrl;

    // Load metadata (bounds width/height/duration)
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('Could not read video metadata')), 4000);
      v.onloadedmetadata = () => { clearTimeout(t); resolve(); };
      v.onerror = () => { clearTimeout(t); reject(new Error('Video file not readable')); };
    });

    // Optional decode probe — quick play/pause (autoplay requires muted)
    try {
      await v.play();
      v.pause();
    } catch {
      throw new Error('This video codec is not decodable on this device.');
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
